import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { ArrowLeft, Copy, Share2, BookOpen, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import CoachMarker from "@/components/CoachMarker";
import MiniMatrix from "@/components/MiniMatrix";
import { DIAGONAL_OPPOSITE, ROLE_COLORS } from "@/lib/focusRoles";
import {
  EuleGlyph,
  BieneGlyph,
  KatzeGlyph,
  HundGlyph,
} from "@/components/FokustypGlyphs";

const GLYPH_BY_SLUG = {
  eule: EuleGlyph,
  biene: BieneGlyph,
  katze: KatzeGlyph,
  hund: HundGlyph,
};
const FOKUSTYP_NAME = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };
// Sprint 4c-3 C.1d — Grammatikalisch korrekter unbestimmter Artikel pro Fokustyp.
const ZIEL_ARTIKEL = { eule: "eine", biene: "eine", katze: "eine", hund: "einen" };

// Sprint 4c-3 C.2 — Frame-Text vor 3 Sätzen (Wording final von Andreas).
function buildFrameText(zielFt) {
  const name = FOKUSTYP_NAME[zielFt] || "diesen Fokustyp";
  return (
    `Du verstehst den ${name}-Anteil am schwersten — weil ihr in der 4-Felder-Matrix ` +
    `diagonal entgegengesetzt steht. Was für ihn selbstverständlich ist, ist für dich ` +
    `anstrengend — und umgekehrt. Genau deshalb ist dieser Flexpfad der wertvollste: ` +
    `wenn du sie meisterst, hast du eine Fähigkeit, die dir die anderen 2 Nachbarn ` +
    `nicht geben können.`
  );
}

export default function DashboardDreisatzBruecke() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dashboard/3-satz-bruecke");
        if (!cancelled) setContent(data);
      } catch (e) {
        if (e?.response?.status === 401) {
          navigate("/login?next=" + encodeURIComponent("/dashboard/3-satz-bruecke"), { replace: true });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const buildText = () => {
    if (!content) return "";
    const lines = content.saetze.map((s) => `${s.nummer}. ${s.text}`);
    const parts = [content.headline, content.subline, "", ...lines];
    if (content.katze_outro) {
      parts.push("", `${content.katze_outro.lead} ${content.katze_outro.punchline}`);
    }
    return parts.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
      setCopied(true);
      toast.success("Flexpfad kopiert.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kopieren fehlgeschlagen.");
    }
  };

  const handleShare = () => {
    const subject = encodeURIComponent("Die Flexpfad");
    const body = encodeURIComponent(buildText());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <DashboardLayout>
      {({ data: layoutData }) => {
        // Sprint 4c-3 — Personalisierung: bestimme Ziel-FT aus User-Primary (diagonal).
        const primarySlug = layoutData?.user?.primary_fokustyp || null;
        const zielSlug = primarySlug ? DIAGONAL_OPPOSITE[primarySlug] : "hund";
        const ZielGlyph = GLYPH_BY_SLUG[zielSlug];
        const crimson = ROLE_COLORS.gegenueber.hex;
        const crimsonBg = ROLE_COLORS.gegenueber.bg;

        return (
        <div className="max-w-2xl mx-auto px-6 lg:px-8 py-12" data-testid="dreisatz-page">
          {/* Sprint 4c-3 C.3 — Coach-Marker (oben links, konsistent mit Hero + Anliegen) */}
          <CoachMarker testid="dreisatz-coach-marker" className="mb-5" />

          <button
            onClick={() => navigate("/cockpit")}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-muted)] hover:text-[var(--fts-primary-bright)] mb-6"
            data-testid="dreisatz-back"
          >
            <ArrowLeft className="w-4 h-4" /> Mitgliederbereich
          </button>

          {loading || !content ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 text-[var(--fts-primary-bright)] animate-spin" />
            </div>
          ) : (
            <>
              <header className="text-center">
                {/* Sprint 4c-3 C.1d — ZielGlyph + Headline in Crimson (Akzent für „Herausforderung").
                    Card-BG bleibt weiß; nur Glyph + Headline-Highlight tragen die Crimson-Rolle. */}
                {ZielGlyph && (
                  <div
                    className="flex justify-center mb-3"
                    data-testid="dreisatz-ziel-glyph"
                    style={{ color: crimson }}
                  >
                    <span
                      className="inline-flex items-center justify-center w-20 h-20 rounded-2xl"
                      style={{ backgroundColor: crimsonBg }}
                    >
                      <ZielGlyph size={56} />
                    </span>
                  </div>
                )}
                <h1
                  className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
                  data-testid="dreisatz-headline"
                >
                  So erreichst du{" "}
                  <span style={{ color: crimson }} data-testid="dreisatz-headline-ziel">
                    {ZIEL_ARTIKEL[zielSlug] || "eine"} {FOKUSTYP_NAME[zielSlug]}
                  </span>
                </h1>
                <p
                  className="mt-3 text-base text-[var(--fts-text-muted)]"
                  data-testid="dreisatz-subline"
                >
                  {content.subline}
                </p>

                {/* Sprint 4c-3 C.2 — Frame-Text (verständlich macht WARUM dieser Flexpfad wertvoll ist) */}
                <div
                  className="mt-6 rounded-xl border p-5 text-left leading-relaxed text-sm"
                  style={{ backgroundColor: crimsonBg + "55", borderColor: crimson + "30" }}
                  data-testid="dreisatz-frame-text"
                >
                  <p className="text-stone-800">{buildFrameText(zielSlug)}</p>
                </div>

                {/* Sprint 4c-3 C.2 — Mini-Matrix-Visual: 4 Quadranten + ICH + ZIEL + diagonale Linie */}
                <div className="mt-6" data-testid="dreisatz-mini-matrix-wrap">
                  <MiniMatrix primarySlug={primarySlug || "eule"} targetSlug={zielSlug} size={200} />
                  <p className="mt-2 text-[11px] text-[var(--fts-text-muted)]">
                    Du (grün) und dein Gegenüber ({FOKUSTYP_NAME[zielSlug]}, rot) stehen diagonal entgegengesetzt.
                  </p>
                </div>

                {content.kontext && (
                  <p
                    className="mt-5 text-sm text-[var(--fts-text-primary)] bg-[var(--fts-neutral-50)] border border-[var(--fts-neutral-200)] rounded-xl p-4 text-left leading-relaxed"
                    data-testid="dreisatz-kontext"
                  >
                    {content.kontext}
                  </p>
                )}
                {content.intro && (
                  <p
                    className="mt-5 text-sm sm:text-base text-[var(--fts-text-primary)] italic leading-relaxed"
                    data-testid="dreisatz-intro"
                  >
                    {content.intro}
                  </p>
                )}
              </header>

              <ol className="mt-10 space-y-6">
                {content.saetze.map((s) => (
                  <li
                    key={s.nummer}
                    className="rounded-2xl border border-[var(--fts-neutral-200)] bg-white p-6"
                    data-testid={`dreisatz-satz-${s.nummer}`}
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
                      Satz {s.nummer}
                    </p>
                    <p className="mt-2 text-2xl font-bold text-[var(--fts-text-primary)] leading-snug">
                      „{s.text}“
                    </p>
                    <p className="mt-3 text-sm italic text-[var(--fts-text-muted)]">
                      [{s.rolle} — {s.erklaerung}]
                    </p>
                  </li>
                ))}
              </ol>

              {content.katze_outro && (
                <div
                  className="mt-8 rounded-2xl bg-[var(--fts-primary-deep)] text-white p-6 text-center"
                  data-testid="dreisatz-katze-outro"
                >
                  <p className="text-sm opacity-80">{content.katze_outro.lead}</p>
                  <p className="mt-1 text-xl font-bold">{content.katze_outro.punchline}</p>
                </div>
              )}

              {content.hinweis && (
                <div
                  className="mt-6 rounded-2xl border-2 border-dashed border-[var(--fts-accent)] bg-amber-50/40 p-5 text-center"
                  data-testid="dreisatz-hinweis"
                >
                  <p className="text-sm text-[var(--fts-text-primary)] leading-relaxed">
                    {content.hinweis}
                  </p>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3 justify-center">
                <button
                  onClick={handleCopy}
                  data-testid="dreisatz-copy-btn"
                  className="inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:bg-[var(--fts-primary-deep)] text-white font-semibold px-5 py-3 text-sm transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Kopiert" : "Kopieren"}
                </button>
                <button
                  onClick={handleShare}
                  data-testid="dreisatz-share-btn"
                  className="inline-flex items-center gap-2 rounded-xl border border-[var(--fts-neutral-200)] bg-white hover:border-[var(--fts-primary-bright)] text-[var(--fts-text-primary)] font-semibold px-5 py-3 text-sm transition-colors"
                >
                  <Share2 className="w-4 h-4" /> Teilen
                </button>
                <button
                  onClick={() => navigate(`/dashboard/fokustyp/${zielSlug}`)}
                  data-testid="dreisatz-more-btn"
                  className="inline-flex items-center gap-2 rounded-xl border border-[var(--fts-neutral-200)] bg-white hover:border-[var(--fts-primary-bright)] text-[var(--fts-text-primary)] font-semibold px-5 py-3 text-sm transition-colors"
                >
                  <BookOpen className="w-4 h-4" /> Mehr erfahren
                </button>
              </div>
            </>
          )}
        </div>
        );
      }}
    </DashboardLayout>
  );
}
